/**
 * Modelo de niveles de un edificio.
 *
 * Los niveles son enteros SIN cero: negativos son subterráneos (N-1, N-2…) y
 * positivos son pisos sobre rasante (N1, N2…). No existe el nivel 0 porque
 * ningún edificio lo nombra así, y meterlo obligaría a filtrarlo en cada
 * recorrido.
 *
 * `nivelesEd` los devuelve en orden físico de abajo hacia arriba, que es el
 * orden que importa: la cobertura vertical se mide por ADYACENCIA EN EL STACK,
 * no por diferencia numérica. Entre N-1 y N1 hay un piso de distancia aunque
 * la resta dé 2.
 *
 * @typedef {object} Building
 * @property {number} pisos    Niveles sobre rasante.
 * @property {number} sub      Cantidad de subterráneos.
 * @property {number[]} tiene  Niveles con DEA del CLIENTE.
 * @property {Record<number, number>} cant  Cuántos DEA del cliente por nivel.
 * @property {number[]} ext    Niveles con DEA EXTERNO (de un tercero).
 * @property {number[]} noPub  Niveles SIN uso público.
 */

/** Niveles del edificio, de abajo hacia arriba: [-sub…-1, 1…pisos]. */
export function buildingLevels(building) {
  const levels = [];
  const sub = building.sub || 0;
  for (let n = -sub; n <= -1; n++) levels.push(n);
  for (let n = 1; n <= (building.pisos || 0); n++) levels.push(n);
  return levels;
}

export function totalLevels(building) {
  return buildingLevels(building).length;
}

/** Etiqueta del nivel tal como se rotula en la lámina. */
export function levelName(level) {
  return `N${level}`;
}

/**
 * Interpreta un nivel escrito por el operador.
 * Acepta "N1", "N-2", "P3", "3", "-1" y "S2" (subterráneo, por compatibilidad
 * con proyectos viejos). Devuelve NaN si no se entiende.
 */
export function parseLevelToken(text) {
  const token = String(text).trim().toUpperCase().replace(/\s+/g, '');
  if (!token) return NaN;

  const basement = token.match(/^S(\d+)$/);
  if (basement) return -Number.parseInt(basement[1], 10);

  const prefixed = token.match(/^[NP](-?\d+)$/);
  if (prefixed) return Number.parseInt(prefixed[1], 10);

  return Number.parseInt(token, 10);
}

/** Tope de subterráneos aceptados al parsear. Evita que un typo cree 900 niveles. */
export const MAX_BASEMENTS = 40;

/**
 * Interpreta una lista de niveles con cantidad opcional.
 * "1, 4x2, -1x3" → { levels: [1, 4, -1], cant: { 4: 2, -1: 3 } }
 *
 * La sintaxis `NxK` existe porque un mismo nivel puede tener varios equipos
 * (ej. un foodtruck y una cinta mecánica en el mismo piso).
 */
export function parseLevelsWithCount(text, maxFloors) {
  const levels = [];
  const cant = {};
  if (!text) return { levels, cant };

  String(text)
    .split(',')
    .forEach((rawToken) => {
      const token = String(rawToken).trim();
      if (!token) return;

      let count = 1;
      let base = token;
      const parts = token.toLowerCase().split('x');
      if (parts.length === 2) {
        base = parts[0];
        count = Math.max(1, Number.parseInt(parts[1], 10) || 1);
      }

      const level = parseLevelToken(base);
      if (Number.isNaN(level) || level === 0) return;
      if (level < -MAX_BASEMENTS || level > maxFloors) return;

      if (!levels.includes(level)) levels.push(level);
      cant[level] = count;
    });

  return { levels, cant };
}

/**
 * Nombres por nivel: "1=Acceso, 4=Casino".
 * Un nivel puede repetirse para nombrar cada equipo por separado
 * ("1=Foodtruck, 1=Cinta mecánica"), así que el valor es SIEMPRE un arreglo.
 */
export function parseLevelNames(text, levelsWithDea = []) {
  const names = {};
  String(text || '')
    .split(',')
    .forEach((part) => {
      const pieces = part.split('=');
      if (pieces.length !== 2) return;
      const level = parseLevelToken(pieces[0]);
      const label = pieces[1].trim();
      if (Number.isNaN(level) || !label) return;
      if (!levelsWithDea.includes(level)) return;
      if (!names[level]) names[level] = [];
      names[level].push(label);
    });
  return names;
}

/**
 * Niveles que comparten su DEA con un equipo de terreno.
 * Acepta "3" (comparte, sin decir con cuál) y "3=1" (comparte con el DEA 1).
 * Normaliza a `{ nivel: nºDEA | null }`.
 */
export function parseSharedLevels(text, levelsWithDea = []) {
  const shared = {};
  String(text || '')
    .split(',')
    .forEach((part) => {
      const pieces = part.split('=');
      const level = parseLevelToken(pieces[0]);
      if (Number.isNaN(level) || !levelsWithDea.includes(level)) return;
      shared[level] =
        pieces.length === 2 ? Number.parseInt(pieces[1].trim(), 10) || null : null;
    });
  return shared;
}

/**
 * Los proyectos guardados con versiones viejas traen `compartidos` como arreglo
 * de niveles. Se normaliza al objeto actual para que abrir un proyecto antiguo
 * no pierda la información de equipos compartidos.
 */
export function normalizeShared(shared) {
  if (Array.isArray(shared)) {
    return Object.fromEntries(shared.map((level) => [level, null]));
  }
  return shared || {};
}
